import React, { useRef, useEffect } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line } from "react-chartjs-2";

const crosshairPlugin = {
  id: "crosshairPlugin",

  afterDatasetsDraw(chart) {
    const { ctx, scales } = chart;

    const meta = chart.getDatasetMeta(2); // Your Entries dataset

    if (!meta?.data?.length) return;

    // Last pink dot
    const point = meta.data[meta.data.length - 1];

    const x = point.x;
    const y = point.y;

    ctx.save();

    ctx.beginPath();
    ctx.strokeStyle = "#ff4081";
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    // Vertical line
    ctx.moveTo(x, scales.y.top);
    ctx.lineTo(x, scales.y.bottom);

    // Horizontal line
    ctx.moveTo(scales.x.left, y);
    ctx.lineTo(scales.x.right, y);

    ctx.stroke();

    ctx.restore();
  }
};


const yAxisLabelPlugin = {
  id: "yAxisLabelPlugin",

  afterDraw(chart) {
    const { ctx, chartArea } = chart;

    const options = chart.options.plugins.yAxisLabel;

    if (!options?.text) return;

    ctx.save();

    ctx.font = `${options.fontSize || 14}px ${options.fontFamily || "Arial"}`;
    ctx.fillStyle = options.color || "#212121";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";

    ctx.fillText(
      options.text,
      chartArea.left - (options.left || 0),
      chartArea.top + (options.top || 0)
    );

    ctx.restore();
  }
};



ChartJS.register(
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
  crosshairPlugin,
  ChartDataLabels,
  yAxisLabelPlugin
);


export default function GrowthDetailsChart({ eleRef, type}) {

    const chartRef = useRef(null);

    useEffect(() => {
      const chart = chartRef.current;

      if (!chart) return;

      setTimeout(() => {
        const meta = chart.getDatasetMeta(2);

        if (!meta?.data?.length) return;

        const lastIndex = meta.data.length - 1;

        chart.setActiveElements([
          {
            datasetIndex: 2,
            index: lastIndex
          }
        ]);

        chart.tooltip.setActiveElements(
          [
            {
              datasetIndex: 2,
              index: lastIndex
            }
          ],
          {
            x: meta.data[lastIndex].x,
            y: meta.data[lastIndex].y
          }
        );

        chart.update();
      }, 100);
    }, [type]);



  // console.log('----------eleref', eleRef);
 
    let age = eleRef.state.selectedChild.Age;
    let latestwt = Number(eleRef.state.selectedChildGrowthDetails?.wt || 0);
    let latestht = Number(eleRef.state.selectedChildGrowthDetails?.ht || 0);
    let latesthc = Number(eleRef.state.selectedChildGrowthDetails?.hc || 0);

    const formattedWT = Number.isInteger(latestwt) ? latestwt : latestwt.toFixed(2);
    const formattedht = Number.isInteger(latestht) ? latestht : latestht.toFixed(2);
    const formattedhc = Number.isInteger(latesthc) ? latesthc : latesthc.toFixed(2);

    const getAgePosition = (age) => {
      if (age === null || age === undefined || age === "") {
          return null;
      }

      if (age === "Birth") {
          return 0;
      }

      const ageStr = String(age).trim().toLowerCase();

      // Extract years and months, including combined ages
      const yearsMatch = ageStr.match(/(\d+(?:\.\d+)?)\s*years?/);
      const monthsMatch = ageStr.match(/(\d+(?:\.\d+)?)\s*months?/);

      if (yearsMatch || monthsMatch) {
          const years = yearsMatch ? Number(yearsMatch[1]) : 0;
          const months = monthsMatch ? Number(monthsMatch[1]) : 0;

          return years * 12 + months;
      }

      const value = parseFloat(ageStr);

      if (isNaN(value)) {
          return null;
      }

      // Days to months
      if (/days?/.test(ageStr)) {
          return value / 30.4375;
      }

      // Weeks to months
      if (/weeks?|wks?/.test(ageStr)) {
          return value / 4.345;
      }

      // Numeric age without a unit: assume months
      return value;
  };

    const mergeLatestData = (historyData, latestValue, latestAge, key) => {
        const history = Array.isArray(historyData)
            ? historyData.map(item => ({
                ...item,
                [key]: Number(item[key])
            }))
            : [];

        const latest = Number(latestValue);

        if (!latest || latest <= 0) {
            return history;
        }

        let normalizedAge;

        if (latestAge === "Birth" || latestAge === "0" || Number(latestAge) === 0 ) {
            normalizedAge = 0;
        } else {
            normalizedAge = latestAge;
        }

        const latestData = {
            age: normalizedAge,
            [key]: latest,
            isLatest: true
        };

        const existingIndex = history.findIndex(item =>
            Number(item[key]) === latest
        );

        if (existingIndex !== -1) {
            history[existingIndex] = {
                ...history[existingIndex],
                ...latestData
            };

            return history;
        }

        return [...history, latestData];
    };

    

    const mergedWeight = mergeLatestData(
        eleRef.state.pastMeasurement?.recent_wt,
        latestwt,
        age,
        "weight"
    );

    const mergedHeight = mergeLatestData(
        eleRef.state.pastMeasurement?.recent_ht,
        latestht,
        age,
        "height"
    );

    const mergedHeadCircumference = mergeLatestData(
        eleRef.state.pastMeasurement?.recent_hd_cir,
        latesthc,
        age,
        "hc"
    );

    // console.log("========== MERGED ==========");
    // console.log("mergedWeight:", mergedWeight);
    // console.log("latestwt:", latestwt);
    // console.log("age:", age);

    // console.log("========== MERGED End ==========");

    // console.log('-----------formattedWT', formattedWT);
    // console.log('--------------age',age);
    // console.log("selectedChildGrowthDetails:", eleRef.state.selectedChildGrowthDetails);
    // console.log("recent_wt:", eleRef.state.pastMeasurement?.recent_wt);
    // console.log("type:", type);

    const valueText =
    type === "ht"
      ? `${formattedht} cm`
      : type === "wt"
      ? `${formattedWT} kg`
      : type === "hc"
      ? `${formattedhc} cm`
      : "";

    const getActualData = (historyData, latestValue, latestAge, key) => {
      const history = Array.isArray(historyData)
          ? historyData.map(item => ({
              ...item,
              age: item.age === "Birth" ? 0 : Number(item.age),
              [key]: Number(item[key])
          }))
          : [];

      const latest = Number(latestValue);
      const ageNumber = latestAge === "Birth"
          ? 0
          : Number(latestAge);

      if (!latest || !ageNumber && ageNumber !== 0) {
          return history;
      }

      // Add latest measurement
      history.push({
          age: ageNumber,
          [key]: latest,
          isLatest: true
      });

      return history;
  };



    const chartConfig = {
    wt: {
        max: eleRef.state.maxValues.map((v, i) => ({
            x: i,
            y: Number(v.weightMax)
        })),

        min: eleRef.state.minValues.map((v, i) => ({
            x: i,
            y: Number(v.weight_min)
        })),

        actual: mergedWeight,

        key: "weight",
        unit: "kg",
        yMax: 30,
        step: 3,
        color: "#ED2B99",
        label: "WEIGHT (kg)",
        fontSize: 14,
        fontFamily: "Roboto-Medium",
        fontColor: "#212121",
        yAxisFontSize: 13,
        yAxisFontFamily: "Roboto-Medium",
        yAxisFontColor: "#212121"
    },

    ht: {
        max: eleRef.state.maxValues.map((v, i) => ({
            x: i,
            y: Number(v.heightMax)
        })),

        min: eleRef.state.minValues.map((v, i) => ({
            x: i,
            y: Number(v.height_min)
        })),

        actual: mergedHeight,

        key: "height",
        unit: "cm",
        yMax: 150,
        step: 20,
        color: "#ED2B99",
        label: "HEIGHT (cm)",
        fontSize: 14,
        fontFamily: "Roboto-Medium",
        fontColor: "#212121"
    },

    hc: {
        max: eleRef.state.maxValues.map((v, i) => ({
            x: i,
            y: Number(v.headCirMax)
        })),

        min: eleRef.state.minValues.map((v, i) => ({
            x: i,
            y: Number(v.head_cir_min)
        })),

        actual: mergedHeadCircumference,

        key: "hc",
        unit: "cm",
        yMax: 100,
        step: 5,
        color: "#ED2B99",
        label: "HEAD CIRCUMFERENCE (cm)",
        fontSize: 14,
        fontFamily: "Roboto-Medium",
        fontColor: "#212121"
    }
};
  
  const cfg = chartConfig[type] || chartConfig["wt"];


  const actualMap = {};

  (cfg.actual || []).forEach(item => {

    const age = getAgePosition(item.age);

    if (age === null) return;

    if (!actualMap[age]) {
        actualMap[age] = [];
    }

    actualMap[age].push(Number(item[cfg.key]));
});


  // const sortedActual = [...(cfg.actual || [])].sort(
  //   (a, b) => Number(a.date) - Number(b.date)
  // );
  
  const sortedActual = [...(cfg.actual || [])].sort((a, b) => {
    const ageA = getAgePosition(a.age);
    const ageB = getAgePosition(b.age);

    if (ageA === null) return 1;
    if (ageB === null) return -1;

    return ageA - ageB;
  });

  const scatterData = [];

  const ageCount = {};

  sortedActual.forEach((item) => {

      const age = getAgePosition(item.age);

      if (age === null) return;

      if (!ageCount[age]) {
          ageCount[age] = 0;
      }

      const index = ageCount[age]++;

      scatterData.push({
          x: age + index * 0.05,
          y: Number(item[cfg.key]),
          id: item.id,
          date: item.date,
          age: item.age,
          isLatest: item.isLatest || false
      });
  });
  

  // console.log("cfg.actual--------:", cfg.actual);
  // console.log("scatterData:", scatterData);

  const data = {
    datasets: [
      {
        label: "Ideal Range(WHO)",
        data: cfg.max,
        borderColor: "#ccc",
        pointRadius: 0,
        order: 1
      },
      {
        label: "Min",
        data: cfg.min,
        borderColor: "#ccc",
        backgroundColor: "rgba(200,200,200,0.4)",
        fill: "-1",
        pointRadius: 0,
        order: 2
      },
      {
        label: "Your Entries",
        data: scatterData,
        backgroundColor: "#ED2B99",
        borderColor: cfg.color,
        showLine: false,
        pointRadius: 5,
        order: 10
      }
    ]
  };


    const minLookup = {};
    const maxLookup = {};
    const actualLookup = {};a

    cfg.min.forEach(p => {
      minLookup[Math.round(p.x)] = p.y;
    });

    cfg.max.forEach(p => {
      maxLookup[Math.round(p.x)] = p.y;
    });


    const currentAge = Math.max(
      0,
      ...Object.keys(actualMap).map(Number)
    );


    const maxChildAge = Math.max(
        0,
        ...Object.keys(actualMap).map(Number)
    );

    const xAxisMin = 0;
    // const xAxisMax = Math.max(6, maxChildAge + 6);

    const xAxisMax = Math.max(6,Math.ceil(maxChildAge + 12));


    const actualMax = Math.max(
        ...(cfg.actual || []).map(item => Number(item[cfg.key]))
    );

    const whoMax = Math.max(
        ...(cfg.max || []).map(item => Number(item.y))
    );

    const dataMax = Math.max(
        actualMax || 0,
        whoMax || 0
    );

    const dynamicYMax = Math.ceil(dataMax / 10) * 10;

    // console.log('----------------dynamicYMax--',dynamicYMax)



    const options = {

      
        responsive: true,
        maintainAspectRatio: false, 
        parsing: false,
        layout: {
          padding: {
            top: 35,
            left: 5,
            right: 5,
            bottom: 5
          }
        },
        interaction: {
            mode: "nearest",
            intersect: false
        },
        scales: {

          x: {
                type: "linear",
                min: xAxisMin,
                max: xAxisMax,

                ticks: {
                  stepSize: 1,
                  minRotation: 0,
                  maxRotation: 0,

                  color: cfg.fontColor,

                  font: {
                    size: cfg.fontSize,
                    family: cfg.fontFamily
                  }
                },

                title: {
                  display: true,
                  text: "MONTHS",
                  color: cfg.fontColor,
                  font: {
                    size: cfg.fontSize,
                    family: cfg.fontFamily
                  }
                }
              },

            y: {
                  min: 0,
                  // max: cfg.yMax,
                  max: dynamicYMax,
                  

                  ticks: {
                    stepSize: cfg.step,

                    color: cfg.yAxisFontColor,

                    font: {
                      size: cfg.yAxisFontSize,
                      family: cfg.yAxisFontFamily
                    }
                  },

                  title: {
                    display: false
                  }
                }
        },
        plugins: {
          yAxisLabel: {
            text: cfg.label,
            color: cfg.fontColor,
            fontSize: cfg.fontSize,
            fontFamily: cfg.fontFamily,

            left: 25,
            top: -35
          },

         

          legend: {
            display: true,
            position: "bottom",
  
            labels: {
              usePointStyle: false,

              boxWidth: 25,
              boxHeight: 15,

              padding: 15,

              font: {
                size: 14,
                family: "Roboto"
              },

              generateLabels: (chart) => {
                return [
                  {
                    text: "Ideal Range (WHO)",
                    fillStyle: "#EEEEEE",
                    strokeStyle: "#EEEEEE",
                    lineWidth: 2,
                    hidden: !chart.isDatasetVisible(0) && !chart.isDatasetVisible(1),
                    datasetIndex: 0
                  },
                  {
                    text: "Your Entries",
                    fillStyle: "#ED2B99",
                    strokeStyle: "#ED2B99",
                    lineWidth: 2,
                    hidden: !chart.isDatasetVisible(2),
                    datasetIndex: 2
                  }
                ];
              }
            },

  onClick: (e, legendItem, legend) => {
    const chart = legend.chart;

    if (legendItem.text === "Ideal Range") {
      // Toggle both min + max together
      const isVisible = chart.isDatasetVisible(0);

      chart.setDatasetVisibility(0, !isVisible);
      chart.setDatasetVisibility(1, !isVisible);
    } else {
      const index = legendItem.datasetIndex;

      chart.setDatasetVisibility(
        index,
        !chart.isDatasetVisible(index)
      );
    }

    chart.update();
  }
}
          
          
          ,
          tooltip: {
            enabled: true,

            callbacks: {
              // title: (ctx) => {
              //   const x = Math.round(ctx[0].raw.x);
              //   return `Month: ${x}`;
              // },
              // title: (ctx) => {
              //     return `Age: ${ctx[0].raw.age}`;
              // },
              title: () => {
                  return "";
              },

              label: (ctx) => {
                const lines = [];

                if (ctx.datasetIndex === 2) {
                  lines.push(`Value: ${ctx.raw.y.toFixed(1)} ${cfg.unit}`);
                }

                const x = Math.round(ctx.raw.x);

                const min = minLookup[x];
                const max = maxLookup[x];

                if (min !== undefined) {
                  lines.push(`Min: ${min.toFixed(1)} ${cfg.unit}`);
                }

                if (max !== undefined) {
                  lines.push(`Max: ${max.toFixed(1)} ${cfg.unit}`);
                }

                return lines;
              }
            }
          },
          datalabels: {
            display: (context) => {
                if (context.datasetIndex !== 2) return false;

                const data = context.dataset.data;

                if (context.dataIndex !== data.length - 1) {
                    return false;
                }

                const point =
                    context.chart.getDatasetMeta(2).data[context.dataIndex];

                if (!point) return false;

                const chartArea = context.chart.chartArea;

                if (
                    point.x < chartArea.left ||
                    point.x > chartArea.right ||
                    point.y < chartArea.top ||
                    point.y > chartArea.bottom
                ) {
                    return false;
                }

                return true;
            },

            anchor: "end",
            align: "right",
            offset: 10,

            formatter: (value) => value.y.toFixed(1),

            color: "#fff",
            backgroundColor: "#ED2B99",
            borderRadius: 4,
            padding: 4,

            // Prevent label from being drawn outside chart area
            clip: true
          }
          
        }
    };

   

    

    // ✅ FINAL RETURN (FIXED)
    return (
        <div className="GrowthmodalChart-overlay">
          <div className="GrowthmodalChart">
              <div className="chart_closeBtn" onClick={eleRef.closeChart}>
                <img src="https://cdn.cdnparenting.com/brainbees/community/vaccination/closeBtn.png" alt="close"></img>
              </div>

              <div className="chart_modal_title">
                  <span >
                      Last updated on:  <span className="chart_date">{ new Date(eleRef.state.selectedChildGrowthDetails?.date)
                                        .toLocaleDateString("en-US", {
                                            month: "long",
                                            day: "numeric",
                                            year: "numeric"
                                        })
                                    } </span>: { valueText }
                  </span>
              </div>

              <div className="chart_container">
                  <Line data={data} options={options} />
                  {/* <Line ref={chartRef} data={data} options={options} /> */}
              </div>

          </div>
        </div>
    );
}

